// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Chip,
  Divider,
} from '@mui/material';
import { BiSearch } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useGetPortalFeaturesQuery } from '../queries/getPortalFeatures.query.generated';
import { useGetOrgUsersQuery } from '../queries/getOrgUsers.query.generated';
import { useGetOrgUsersCountQuery } from '../queries/getOrgUsersCount.query.generated';
import { useUpsertUserFeaturesMutation } from '../queries/upsertUserFeatures.mutation.generated';
import { useDeleteUserFeatureMutation } from '../queries/deleteUserFeature.mutation.generated';
import { useUser, useCustomerTierMap } from '@store';
import { useDebounce } from '@hooks/useDebounce';
import UserCard from './UserCard';
import { buildFeatureTree, buildFeatureMap, getChildIdsByRoot } from '../utils/featureTreeUtils';
import { buildScopedUsersWhere } from '../utils/whereClauseBuilders';
import {
  toggleFeatureInSet,
  getRootCheckState as computeRootCheckState,
  getFeaturesToAdd,
  getFeaturesToRemove,
  userHasAllFeatures,
} from '../utils/featureSelectionUtils';
import { buildFeatureUpsertObjects } from '../utils/mutationPayloadUtils';
import { isSuperAdmin, hasDriverRole } from '../utils/roleUtils';
import type { Feature } from '../types';

// TYPES ---------------------------------------------------------------- //

interface BatchPermissionsPanelProps {
  customerIds: number[];
}

interface OrgUser {
  id: number;
  display_name: string;
  email: string;
  avatar_url: string | null;
  default_role: string;
  active: boolean;
  users_usertofeatures: {
    id: number;
    feature: { id: number; name: string; label: string };
  }[];
  driver: { id: number; status: string } | null;
}

type SortOrder = 'asc' | 'desc';

// COMPONENT ---------------------------------------------------------------- //

const BatchPermissionsPanel: React.FC<BatchPermissionsPanelProps> = ({ customerIds }) => {
  const currentUser = useUser();
  const currentUserEmail = currentUser?.profile?.email || '';
  const customerTierMap = useCustomerTierMap();
  const [upsertFeatures] = useUpsertUserFeaturesMutation();
  const [removeFeature] = useDeleteUserFeatureMutation();

  // Feature selection (multi-select)
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<number>>(new Set());

  // User table state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('display_name');
  const [order, setOrder] = useState<SortOrder>('asc');

  const debouncedSearch = useDebounce(search.trim(), 300);
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  // Features query — filter by tiers available across the managed rooftops
  const { data: featuresData } = useGetPortalFeaturesQuery({ fetchPolicy: 'cache-first' });
  const rawFeatures: Feature[] = (featuresData?.features as Feature[]) ?? [];

  const availableTiers = useMemo(() => {
    const tiers = new Set<string>();
    for (const cid of customerIds) {
      const t = customerTierMap[cid];
      if (t) tiers.add(t);
    }
    return tiers;
  }, [customerIds, customerTierMap]);

  const allFeatures = useMemo(() => {
    if (availableTiers.size === 0) return rawFeatures;
    return rawFeatures.filter(f => {
      if (!f.config?.tiers?.length) return true; // global → always show
      return f.config.tiers.some(t => availableTiers.has(t));
    });
  }, [rawFeatures, availableTiers]);

  const tree = useMemo(() => buildFeatureTree(allFeatures), [allFeatures]);
  const featureMap = useMemo(() => buildFeatureMap(allFeatures), [allFeatures]);
  const childIdsByRoot = useMemo(() => getChildIdsByRoot(tree), [tree]);

  // Auto-select first root feature
  useEffect(() => {
    if (tree.length > 0 && selectedFeatureIds.size === 0) {
      setSelectedFeatureIds(new Set([tree[0].root.id]));
    }
  }, [tree, selectedFeatureIds.size]);

  // Feature toggle handler
  const handleFeatureToggle = useCallback(
    (featureId: number) => {
      setSelectedFeatureIds(prev => toggleFeatureInSet(featureId, prev, tree));
    },
    [tree],
  );

  // Root checkbox state helper
  const getRootCheckState = useCallback(
    (rootId: number, childIds: number[]) => computeRootCheckState(rootId, childIds, selectedFeatureIds),
    [selectedFeatureIds],
  );

  // Users query
  const where = useMemo(
    () => buildScopedUsersWhere(customerIds, { search: debouncedSearch || undefined, showInactive: true }),
    [customerIds, debouncedSearch],
  );

  const gqlOrderBy = useMemo(() => [{ [orderBy]: order }], [orderBy, order]);

  const { data, loading, previousData, refetch } = useGetOrgUsersQuery({
    variables: { where, orderBy: gqlOrderBy, limit: rowsPerPage, offset: page * rowsPerPage },
    skip: customerIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const displayData = data ?? previousData;

  const { data: countData } = useGetOrgUsersCountQuery({
    variables: { where },
    skip: customerIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const users: OrgUser[] = (displayData?.users as OrgUser[]) ?? [];
  const totalCount = countData?.users_aggregate?.aggregate?.count ?? 0;

  // Optimistic state
  const [optimistic, setOptimistic] = useState<Map<number, boolean>>(new Map());

  // Clear optimistic state when feature selection changes
  useEffect(() => {
    setOptimistic(new Map());
  }, [selectedFeatureIds]);

  // Clear optimistic entries matching server state after refetch
  useEffect(() => {
    if (!data?.users || selectedFeatureIds.size === 0) return;
    const selectedArr = Array.from(selectedFeatureIds);
    setOptimistic(prev => {
      const next = new Map(prev);
      let changed = false;
      for (const [userId, value] of prev) {
        const user = (data.users as OrgUser[]).find((u: OrgUser) => u.id === userId);
        if (!user) continue;
        const hasAll = selectedArr.every(
          fid => user.users_usertofeatures.some(uf => uf.feature.id === fid),
        );
        if (hasAll === value) {
          next.delete(userId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data, selectedFeatureIds]);

  const userHasAllSelected = useCallback(
    (user: OrgUser): boolean => {
      const opt = optimistic.get(user.id);
      if (opt !== undefined) return opt;
      const userFeatureIds = user.users_usertofeatures.map(uf => uf.feature.id);
      return userHasAllFeatures(Array.from(selectedFeatureIds), userFeatureIds);
    },
    [optimistic, selectedFeatureIds],
  );

  // Toggle handler (apply/remove template)
  const handleToggle = useCallback(
    async (user: OrgUser, checked: boolean) => {
      const featureIds = Array.from(selectedFeatureIds);
      if (featureIds.length === 0) return;

      setOptimistic(prev => new Map(prev).set(user.id, checked));

      try {
        const userFeatureIds = user.users_usertofeatures.map(uf => uf.feature.id);

        if (checked) {
          const toAdd = getFeaturesToAdd(featureIds, userFeatureIds);
          if (toAdd.length > 0) {
            await upsertFeatures({
              variables: {
                objects: buildFeatureUpsertObjects(toAdd, user.id, currentUserEmail),
              },
            });
          }
        } else {
          // Guard: prevent removing user-management from super admins
          if (isSuperAdmin(user.default_role)) {
            const removingLocked = featureIds.some(fid => {
              const feat = featureMap.get(fid);
              return feat?.name === 'user-management';
            });
            if (removingLocked) {
              setOptimistic(prev => {
                const next = new Map(prev);
                next.delete(user.id);
                return next;
              });
              toast.warning('Cannot remove User Management from a Super Admin');
              return;
            }
          }

          const toRemove = getFeaturesToRemove(featureIds, userFeatureIds, childIdsByRoot);
          await Promise.all(
            toRemove.map(fid =>
              removeFeature({ variables: { userId: user.id, featureId: fid } }),
            ),
          );
        }

        refetch();
      } catch (err: any) {
        setOptimistic(prev => {
          const next = new Map(prev);
          next.delete(user.id);
          return next;
        });
        toast.error(`Failed to update features: ${err.message}`);
      }
    },
    [selectedFeatureIds, currentUserEmail, childIdsByRoot, featureMap, upsertFeatures, removeFeature, refetch],
  );

  // Sort handler
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  // Selected features summary
  const selectedLabels = useMemo(
    () => Array.from(selectedFeatureIds).map(id => featureMap.get(id)?.label).filter(Boolean) as string[],
    [selectedFeatureIds, featureMap],
  );

  return (
    <Box sx={{ display: 'flex', height: 600, border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
      {/* Left panel — Feature tree with checkboxes */}
      <Box
        sx={{
          width: '40%',
          minWidth: 240,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Feature Template
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            Select features to apply as a set
          </Typography>
        </Box>
        <List dense disablePadding>
          {tree.map(({ root, children }, groupIdx) => {
            const childIds = children.map(c => c.id);
            const { checked: rootChecked, indeterminate: rootIndeterminate } = getRootCheckState(root.id, childIds);

            return (
              <React.Fragment key={root.id}>
                {groupIdx > 0 && <Divider />}
                <ListItemButton
                  onClick={() => handleFeatureToggle(root.id)}
                  sx={{ py: 0.75, px: 1 }}
                >
                  <Checkbox
                    edge="start"
                    checked={rootChecked}
                    indeterminate={rootIndeterminate}
                    size="small"
                    tabIndex={-1}
                    disableRipple
                    sx={{ mr: 0.5 }}
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{root.label}</span>
                        {availableTiers.size > 1 && root.config?.tiers?.length && (
                          <Chip label="Lite" size="small" color="info"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                        )}
                      </Box>
                    }
                    secondary={root.description || undefined}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                  />
                </ListItemButton>
                {children.map(child => (
                  <ListItemButton
                    key={child.id}
                    onClick={() => handleFeatureToggle(child.id)}
                    sx={{ py: 0.5, pl: 3, pr: 1 }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedFeatureIds.has(child.id)}
                      size="small"
                      tabIndex={-1}
                      disableRipple
                      sx={{ mr: 0.5 }}
                    />
                    <ListItemText
                      primary={child.label}
                      secondary={child.description || undefined}
                      primaryTypographyProps={{ fontSize: 12, color: 'text.secondary' }}
                      secondaryTypographyProps={{ fontSize: 10.5, noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Right panel — User list */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {selectedFeatureIds.size === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Select features from the left panel.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {selectedLabels.map(label => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 500,
                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                      color: '#1d4ed8',
                      border: 'none',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users by name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <BiSearch size={18} color="#778088" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading && !displayData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : users.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>No users found.</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'display_name'}
                              direction={orderBy === 'display_name' ? order : 'asc'}
                              onClick={() => handleSort('display_name')}
                            >
                              User
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Features</TableCell>
                          <TableCell sx={{ width: 70 }}>Apply</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map(user => {
                          const hasAll = userHasAllSelected(user);
                          const featureChips = user.users_usertofeatures.slice(0, 3);
                          const overflow = user.users_usertofeatures.length - 3;
                          return (
                            <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell>
                                <UserCard
                                  avatarUrl={user.avatar_url}
                                  displayName={user.display_name}
                                  email={user.email}
                                  isDriver={hasDriverRole((user as any).users_usertoroles)}
                                  driverStatus={user.driver?.status}
                                  compact
                                />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {featureChips.map(uf => (
                                    <Chip
                                      key={uf.feature.id}
                                      label={uf.feature.label}
                                      size="small"
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                  ))}
                                  {overflow > 0 && (
                                    <Chip
                                      label={`+${overflow}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant={hasAll ? 'outlined' : 'contained'}
                                  color={hasAll ? 'error' : 'primary'}
                                  onClick={() => handleToggle(user, !hasAll)}
                                  disabled={selectedFeatureIds.size === 0}
                                  sx={{ textTransform: 'none', fontSize: 12, minWidth: 70, py: 0.25 }}
                                >
                                  {hasAll ? 'Remove' : 'Apply'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default BatchPermissionsPanel;
